import { useId, type ReactNode } from "react";
import type { BuyerSequenceRenderedDto } from "@/api/buyerSequence";
import { Button, Card } from "@/design-system/components/ui";
import { MissingRequiredNotice, UnavailableNotice } from "./CardNotices";
import type { MissingRequiredSummary } from "./outreachCopy";
import { OUTREACH_CARD_HEADING } from "./PulledFromCard";
import { RenderedBlock } from "./RenderedBlock";

/**
 * The Email card — pasted into the specialist's own mail client and sent as
 * their own correspondence.
 *
 * <p>"Include my signature" defaults ON because most of the book is worked from
 * a client that adds nothing; the hint says when to turn it off, because a
 * client that appends its own signature under a pasted one sends the buyer two.
 * What COPY EMAIL puts on the clipboard is assembled by `assembleCopiedEmail`
 * (see its note on why no Phoenix role statement or footer is appended).</p>
 */
export function EmailCard({
  address,
  subject,
  body,
  signature,
  includeSignature,
  onToggleSignature,
  unavailable,
  missing,
  busy,
  copied,
  onCopyEmail,
  onCopyAddress,
  onCopySubject,
  signatureNotice,
}: {
  address: string | null;
  /** Null when the step cannot be written — `unavailable` then says why. */
  subject: BuyerSequenceRenderedDto | null;
  body: BuyerSequenceRenderedDto | null;
  signature: BuyerSequenceRenderedDto;
  includeSignature: boolean;
  onToggleSignature: (next: boolean) => void;
  unavailable: string | null;
  /** Subject, body, and the signature while it is included. */
  missing: MissingRequiredSummary;
  busy: boolean;
  copied: boolean;
  onCopyEmail: () => void;
  onCopyAddress: () => void;
  onCopySubject: () => void;
  /** The team-changed notice for the signature wording, rendered beside it. */
  signatureNotice?: ReactNode;
}) {
  const signatureId = useId();
  const blocked = missing.profileNouns !== null || missing.otherLabels.length > 0;
  const ready = subject !== null && body !== null;

  return (
    <Card data-testid="outreach-email-card">
      <h3 className={OUTREACH_CARD_HEADING}>Email</h3>

      <div className="mt-space-3 flex flex-wrap items-center justify-between gap-space-2">
        <span className="min-w-0 break-words text-small text-onyx" data-testid="outreach-email-address">
          {address ?? <span className="text-gray-mid">No email on file</span>}
        </span>
        <Button
          variant="text"
          size="sm"
          onClick={onCopyAddress}
          disabled={!address || !ready || busy}
          data-testid="outreach-copy-address"
        >
          Copy address
        </Button>
      </div>

      <div className="mt-space-3 grid gap-space-3">
        {!ready ? (
          <UnavailableNotice reason={unavailable ?? ""} testId="outreach-email-unavailable" />
        ) : (
          <>
            <div className="flex flex-wrap items-start justify-between gap-space-2">
              <div className="grid min-w-0 flex-1 gap-space-1">
                <span className="text-caption uppercase tracking-wide text-gray-mid">Subject</span>
                <RenderedBlock block={subject} data-testid="outreach-email-subject" />
              </div>
              <Button
                variant="text"
                size="sm"
                onClick={onCopySubject}
                disabled={busy || blocked}
                data-testid="outreach-copy-subject"
              >
                Copy subject
              </Button>
            </div>

            <div className="grid gap-space-2 rounded-md border border-border-light bg-white-warm px-space-3 py-space-2">
              <RenderedBlock block={body} data-testid="outreach-email-body" />
              {includeSignature && (
                <RenderedBlock
                  block={signature}
                  className="border-t border-border-light pt-space-2"
                  data-testid="outreach-email-signature"
                />
              )}
            </div>

            <div className="grid gap-space-1">
              <label
                htmlFor={signatureId}
                className="flex flex-wrap items-center gap-space-2 text-small text-onyx"
              >
                <input
                  id={signatureId}
                  type="checkbox"
                  checked={includeSignature}
                  onChange={(e) => onToggleSignature(e.target.checked)}
                  data-testid="outreach-include-signature"
                />
                Include my signature
                <span className="text-caption text-gray-mid">
                  (turn off if Gmail already adds it)
                </span>
              </label>
              {signatureNotice}
            </div>

            <MissingRequiredNotice summary={missing} testId="outreach-email-missing" />
          </>
        )}

        <div>
          <Button
            onClick={onCopyEmail}
            disabled={!ready || busy || blocked}
            data-testid="outreach-copy-email"
          >
            {copied ? "Copied ✓" : "Copy email"}
          </Button>
        </div>
      </div>
    </Card>
  );
}
